import React, { useState, useEffect, useCallback } from 'react';
import { nlcCalculate } from '../../apis/NlcApi';
import { BrandsApi } from '../../apis/BrandsApi';
import { ProductsApi } from '../../apis/ProductsApi';
import { SchemesApi } from '../../apis/SchemesApi';
import { 
  Box, 
  Typography, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Grid,
  Button,
  InputAdornment
} from '@mui/material';
import { toast } from "react-toastify";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularIndeterminateModal from "../../utils/Loading";
import { useDispatch, useSelector } from "react-redux";
import { setLoadingPanel } from "../../redux/features/loading";


const NlcCalculator = () => {
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [availableSchemes, setAvailableSchemes] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [schemeRows, setSchemeRows] = useState([]);
  const [netLandingPrice, setNetLandingPrice] = useState(null);
  const isLoading = useSelector((state) => state.loadingReducer.isLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    const loadBrands = async () => {
      try {
        dispatch(setLoadingPanel(true));
        const response = await BrandsApi.GetBrandsListByFilter({
          page: 0,
          size: 100
        });
        if (response && response.data && response.data.data) {
          setBrands(response.data.data);
        } else {
          setBrands([]);
        }
        dispatch(setLoadingPanel(false));
      } catch (error) {
        console.error('Error fetching brands:', error);
        dispatch(setLoadingPanel(false));
      }
    };
    
    loadBrands();
  }, [dispatch]);

  const fetchProducts = useCallback(async () => {
    try {
      dispatch(setLoadingPanel(true));
      const response = await ProductsApi.GetProductsListByFilter({
        page: 0,
        size: 100,
        brandId: selectedBrand,
        isActive: true
      });
      if (response && response.data && response.data.data) {
        setProducts(response.data.data);
      } else {
        setProducts([]);
      }
      dispatch(setLoadingPanel(false));
    } catch (error) {
      console.error('Error fetching products:', error);
      dispatch(setLoadingPanel(false));
    }
  }, [selectedBrand, dispatch]);

  const fetchSchemes = useCallback(async () => {
    try {
      dispatch(setLoadingPanel(true));

      const today = new Date();

      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
 
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      const formatDate = (date) => {
        return date.toISOString().split('T')[0];
      };
      
      const response = await SchemesApi.GetByFilters({
        page: 0,
        size: 100,
        brandId: selectedBrand,
        productId: selectedProduct,  
        periodFrom: formatDate(firstDay),
        periodTo: formatDate(lastDay)
      });
      
      if (response && response.data && response.data.data) {
        setAvailableSchemes(response.data.data);
      } else {
        setAvailableSchemes([]);
      }
      dispatch(setLoadingPanel(false));
    } catch (error) {
      console.error('Error fetching schemes:', error);
      dispatch(setLoadingPanel(false));
    }
  }, [selectedBrand, selectedProduct, dispatch]);

  useEffect(() => {
    if (selectedBrand) {
      fetchProducts();
      if (selectedProduct) {
        fetchSchemes();
      }
    }
  }, [selectedBrand, selectedProduct, fetchProducts, fetchSchemes]);

  const fetchBrands = useCallback(async () => {
    try {
      dispatch(setLoadingPanel(true));
      const response = await BrandsApi.GetBrandsListByFilter({
        page: 0,
        size: 100
      });
      if (response && response.data && response.data.data) {
        setBrands(response.data.data);
      } else {
        setBrands([]);
      }
      dispatch(setLoadingPanel(false));
    } catch (error) {
      console.error('Error fetching brands:', error);
      dispatch(setLoadingPanel(false));
    }
  }, [dispatch]);

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
    setSelectedProduct('');
  };

  const handleProductChange = (event) => {
    setSelectedProduct(event.target.value);
  };

  const handleAddSchemeRow = () => {
    const newRow = { 
      isCustom: true, 
      name: '', 
      periodFrom: '',
      periodTo: '',
      payoutType: 'PERCENTAGE', 
      value: '', 
      deduction: '' 
    };
    setSchemeRows([...schemeRows, newRow]);
  };
  
  const handleAddExistingScheme = (scheme) => {

    const newScheme = {
      id: scheme.id || scheme._id || scheme.schemeId || null,
      name: scheme.name || scheme.schemeName || '',
      periodFrom: scheme.periodFrom || scheme.startDate || '',
      periodTo: scheme.periodTo || scheme.endDate || '',
      payoutType: 'PERCENTAGE', 
      value: '',
      deduction: '' 
    };
    
    setSchemeRows([...schemeRows, newScheme]);
  };

  const handleRemoveSchemeRow = (rowIdentifier) => {

    if (typeof rowIdentifier === 'string' && rowIdentifier.startsWith('custom-')) {

      const index = parseInt(rowIdentifier.split('-')[1]);
      setSchemeRows(schemeRows.filter((_, i) => i !== index));
    } else {
  
      setSchemeRows(schemeRows.filter(row => row.id !== rowIdentifier));
    }
  };

  const handleSchemeChange = (rowIdentifier, field, value) => {
    
    if (field === 'deduction') {
      const row = typeof rowIdentifier === 'string' && rowIdentifier.startsWith('custom-')
        ? schemeRows[parseInt(rowIdentifier.split('-')[1])]
        : schemeRows.find(r => r.id === rowIdentifier);
      
      if (row && row.payoutType === 'PERCENTAGE') {
     
        const numValue = parseFloat(value);
        
        if (!isNaN(numValue) && numValue > 100) {
          toast('Percentage cannot be more than 100%', { type: "warn" });
          return; 
        }
      }
    }
    
    if (typeof rowIdentifier === 'string' && rowIdentifier.startsWith('custom-')) {
      
      const index = parseInt(rowIdentifier.split('-')[1]);
      setSchemeRows(schemeRows.map((row, i) => 
        i === index ? { ...row, [field]: value } : row
      ));
    } else {
      
      setSchemeRows(schemeRows.map(row => 
        row.id === rowIdentifier ? { ...row, [field]: value } : row
      ));
    }
  };

  const calculateNLC = async () => {
    
    if (!selectedBrand || !selectedProduct) {
      alert('Please select both Brand and Model before calculating');
      return;
    }
    
    try {
      dispatch(setLoadingPanel(true));
      const schemesPayload = schemeRows.map(row => {
        const selectedBrandObj = brands.find(b => 
          (b.id === selectedBrand) || 
          (b._id === selectedBrand) || 
          (b.brandId === selectedBrand)
        );
        
        
        let schemePayload = {
          
          ...(row.isCustom ? {} : { id: row.id ? row.id.toString() : undefined }),
          name: row.name,
          brand: {
            id: selectedBrand,
            name: selectedBrandObj?.name || selectedBrandObj?.brandName || ''
          },
          
          brandId: selectedBrand,
          productId: selectedProduct,
         
          periodFrom: row.periodFrom,
          periodTo: row.periodTo,
          payoutType: row.payoutType
        };
        
        
        if (row.payoutType === 'FLAT') {
          schemePayload.amount = parseFloat(row.deduction) || 0;
        } else {
          schemePayload.value = row.deduction;
        }
        
        return schemePayload;
      });

      const payload = {
        brandId: selectedBrand,
        productId: selectedProduct,
        schemes: schemesPayload
      };

      console.log('Sending payload:', payload);

      const response = await nlcCalculate(payload);
      console.log('Response received:', response);
      
      if (response && response.data) {
        
        if (response.data.data && response.data.data.data && response.data.data.data.nlc !== undefined) {
         
          setNetLandingPrice(response.data.data.data.nlc);
        } else if (response.data.data && response.data.data.nlc !== undefined) {
          
          setNetLandingPrice(response.data.data.nlc);
        } else if (response.data.netLandingPrice !== undefined) {
          
          setNetLandingPrice(response.data.netLandingPrice);
        } else if (typeof response.data === 'number') {
          
          setNetLandingPrice(response.data);
        } else {
          console.error('Unexpected response format:', response.data);
          setNetLandingPrice(0);
        }
      } else {
        console.error('No response data received');
        setNetLandingPrice(0);
      }
      dispatch(setLoadingPanel(false));
    } catch (error) {
      console.error('Error calculating NLC:', error);
      dispatch(setLoadingPanel(false));
    }
  };

  return (
    <>
    <CircularIndeterminateModal open={isLoading} />
    <Paper elevation={3} sx={{ p: 4, maxWidth: '900px', mx: 'auto', my: 4, position: 'relative', zIndex: 1000 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight="bold">
        NET LANDING PRICE CALCULATOR
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="brand-label">Brand</InputLabel>
          <Select
            labelId="brand-label"
            value={selectedBrand}
            label="Brand"
            onChange={handleBrandChange}
          >
            {brands && brands.length > 0 ? brands.map((brand) => {
               
              const brandId = brand.id || brand._id || brand.brandId;
              const brandName = brand.name || brand.brandName || brand.label;
              
              return (
                <MenuItem key={brandId} value={brandId}>
                  {brandName}
                </MenuItem>
              );
            }) : null}
          </Select>
        </FormControl>
        
       
        {selectedBrand && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="model-label">Model</InputLabel>
            <Select
              labelId="model-label"
              value={selectedProduct}
              label="Model"
              onChange={handleProductChange}
            >
              {products && products.length > 0 ? products.map((product) => {
                const productId = product.id || product._id || product.productId;
                const productName = product.primaryName || product.name || product.label;
                
                return (
                  <MenuItem key={productId} value={productId}>
                    {productName}
                  </MenuItem>
                );
              }) : null}
            </Select>
          </FormControl>
        )}
        
      
      </Box>
      
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, overflow: 'auto', maxWidth: '100%' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Scheme Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Start Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>End Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Payout Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Scheme Amount</TableCell>
              <TableCell width="50px"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schemeRows.map((row, index) => (
              <TableRow key={row.id || `custom-${index}`}>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.name}
                    onChange={(e) => handleSchemeChange(row.id || `custom-${index}`, 'name', e.target.value)}
                    placeholder="Scheme Title"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Start Date"
                    value={row.periodFrom}
                    onChange={(e) => handleSchemeChange(row.id || `custom-${index}`, 'periodFrom', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="End Date"
                    value={row.periodTo}
                    onChange={(e) => handleSchemeChange(row.id || `custom-${index}`, 'periodTo', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    fullWidth
                    size="small"
                    value={row.payoutType}
                    onChange={(e) => handleSchemeChange(row.id || `custom-${index}`, 'payoutType', e.target.value)}
                  >
                    <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                    <MenuItem value="FLAT">Flat</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="text"
                    value={row.deduction}
                    onChange={(e) => handleSchemeChange(row.id || `custom-${index}`, 'deduction', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    onClick={() => handleRemoveSchemeRow(row.id || `custom-${index}`)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3 
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 2,
          width: { xs: '100%', sm: 'auto' }
        }}>
          {schemeRows.length === 0 && (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              Please select or add a scheme
            </Typography>
          )}
          
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={handleAddSchemeRow}
            sx={{ minWidth: '150px' }}
            fullWidth={false}
          >
            Add Custom
          </Button>
          
          <FormControl sx={{ minWidth: { xs: '100%', sm: 250 } }}>
            <InputLabel id="available-schemes-label">Available Schemes</InputLabel>
            <Select
              labelId="available-schemes-label"
              label="Available Schemes"
              value=""
              onChange={(e) => {
                const selectedScheme = availableSchemes.find(s => 
                  (s.id === e.target.value) || 
                  (s._id === e.target.value) || 
                  (s.schemeId === e.target.value)
                );
                if (selectedScheme) {
                  handleAddExistingScheme(selectedScheme);
                }
              }}
              displayEmpty
              startAdornment={<AddIcon style={{ marginRight: '8px', color: '#757575' }} />}
            >
              {availableSchemes && availableSchemes.length > 0 ? availableSchemes.map((scheme) => {
                const schemeId = scheme.id || scheme._id || scheme.schemeId;
                const schemeName = scheme.name || scheme.schemeName || scheme.label;
                
                return (
                  <MenuItem key={schemeId} value={schemeId}>
                    {schemeName}
                  </MenuItem>
                );
              }) : null}
            </Select>
          </FormControl>
        </Box>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={calculateNLC}
        >
          Calculate
        </Button>
      </Box>
      
      {netLandingPrice !== null && (
        <Box sx={{ textAlign: 'right', mt: 2 }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            Net Landing Price
          </Typography>
          <Typography variant="h4" component="div" fontWeight="bold">
            ₹ {netLandingPrice.toLocaleString('en-IN')}
          </Typography>
        </Box>
      )}
    </Paper>
    </>
  );
};

export default NlcCalculator;